import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  XMarkIcon,
  UserPlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  UserIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  TagIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useAuth } from "../../../contexts/AuthContext";
import { createCase, getClientsByAssignedLawyer, createClient } from "@/shared/db";
import { toast } from "sonner";
import { Button, Input, Select, Textarea } from "@/shared/ui";
import { useCountries } from "@/hooks/useCountries";
import type { CaseFormData } from "../types/case.types";

interface CreateCaseModalProps {
  isOpen?: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedClientId?: string;
}

interface ClientData {
  id: string;
  profiles: {
    full_name?: string;
    email?: string;
  };
}

interface NewClientForm {
  full_name: string;
  email: string;
  phone: string;
  country: string;
  client_type: "individual" | "business" | "nonprofit";
  company_name: string;
}

const STEPS = [
  { id: 1, name: "client", icon: UserIcon },
  { id: 2, name: "details", icon: BriefcaseIcon },
  { id: 3, name: "additional", icon: DocumentTextIcon },
];

export const CreateCaseModal: React.FC<CreateCaseModalProps> = ({
  isOpen = false,
  onClose,
  onSuccess,
  preselectedClientId,
}) => {
  const { t, i18n } = useTranslation();
  const { user, profile } = useAuth();
  const { countries } = useCountries();

  // Step control
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clients, setClients] = useState<ClientData[]>([]);

  // New client mode
  const [isCreatingNewClient, setIsCreatingNewClient] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState<NewClientForm>({
    full_name: "",
    email: "",
    phone: "",
    country: profile?.country || "MX",
    client_type: "individual",
    company_name: "",
  });

  // Case form data
  const [formData, setFormData] = useState({
    clientId: preselectedClientId || "",
    title: "",
    description: "",
    practiceArea: "",
    priority: "medium",
    estimatedHours: "",
    tags: [] as string[],
    notes: "",
  });

  // Tags input
  const [tagInput, setTagInput] = useState("");

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setIsCreatingNewClient(false);
      setFormData({
        clientId: preselectedClientId || "",
        title: "",
        description: "",
        practiceArea: "",
        priority: "medium",
        estimatedHours: "",
        tags: [],
        notes: "",
      });
      setNewClientForm({
        full_name: "",
        email: "",
        phone: "",
        country: profile?.country || "MX",
        client_type: "individual",
        company_name: "",
      });
      setTagInput("");
      loadClients();
    }
  }, [isOpen, preselectedClientId, profile?.country]);

  const loadClients = async () => {
    if (!user) return;
    setClientsLoading(true);
    try {
      const data = await getClientsByAssignedLawyer(user.id);
      setClients(data as unknown as ClientData[]);
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setClientsLoading(false);
    }
  };

  const practiceAreas = [
    { code: "civil", name: t("lawyer.practiceAreas.civil") },
    { code: "penal", name: t("lawyer.practiceAreas.penal") },
    { code: "laboral", name: t("lawyer.practiceAreas.laboral") },
    { code: "comercial", name: t("lawyer.practiceAreas.comercial") },
    { code: "fiscal", name: t("lawyer.practiceAreas.fiscal") },
    { code: "administrativo", name: t("lawyer.practiceAreas.administrativo") },
    { code: "familiar", name: t("lawyer.practiceAreas.familiar") },
    {
      code: "inmobiliario",
      name: t("lawyer.practiceAreas.inmobiliario"),
    },
    {
      code: "propiedad_intelectual",
      name: t("lawyer.practiceAreas.propiedad_intelectual"),
    },
    {
      code: "migratorio",
      name: t("lawyer.practiceAreas.migratorio"),
    },
    {
      code: "ambiental",
      name: t("lawyer.practiceAreas.ambiental"),
    },
    {
      code: "corporativo",
      name: t("lawyer.practiceAreas.corporativo"),
    },
  ];

  const handleClose = () => {
    if (!loading && !creatingClient) {
      onClose();
    }
  };

  const handleCreateNewClient = async () => {
    if (!user || !newClientForm.full_name || !newClientForm.email) {
      toast.error(t("lawyer.createCaseModal.clientForm.requiredFields"));
      return;
    }

    setCreatingClient(true);
    try {
      const newClient = await createClient(user.id, {
        full_name: newClientForm.full_name,
        email: newClientForm.email,
        phone: newClientForm.phone || undefined,
        country: newClientForm.country,
        client_type: newClientForm.client_type,
        company_name:
          newClientForm.client_type !== "individual"
            ? newClientForm.company_name
            : undefined,
      });

      // Reload clients and select the new one
      await loadClients();
      setFormData((prev) => ({ ...prev, clientId: newClient.id }));
      setIsCreatingNewClient(false);
      toast.success(t("lawyer.createCaseModal.clientCreated"));

      // Track client creation
      import("@/utils/analytics").then(({ analytics }) => {
        analytics.clientAdded();
      });
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error(t("lawyer.createCaseModal.clientError"));
    } finally {
      setCreatingClient(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const canProceedToStep2 = formData.clientId !== "";
  const canProceedToStep3 =
    formData.title !== "" && formData.practiceArea !== "";

  const handleSubmit = async () => {
    if (!user || !profile) return;

    if (!formData.clientId || !formData.title || !formData.practiceArea) {
      toast.error(t("lawyer.createCaseModal.requiredFields"));
      return;
    }

    setLoading(true);
    try {
      await createCase({
        lawyer_id: user.id,
        client_id: formData.clientId,
        title: formData.title,
        description: formData.description,
        practice_area: formData.practiceArea,
        priority: formData.priority as "low" | "medium" | "high" | "urgent",
        country: profile.country || "MX",
        status: "pending",
        estimated_hours: formData.estimatedHours
          ? parseFloat(formData.estimatedHours)
          : undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes
          ? [
              {
                content: formData.notes,
                created_at: new Date().toISOString(),
                author_id: user.id,
                author_name: profile.full_name || "",
              },
            ]
          : undefined,
      });

      toast.success(t("lawyer.createCaseModal.success"));

      // Track case creation
      import("@/utils/analytics").then(({ analytics }) => {
        analytics.caseCreated();
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error creating case:", error);
      toast.error(t("lawyer.createCaseModal.error"));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Background overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full z-10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div className="bg-linear-to-r from-tiber-700 to-tiber-500 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {t("lawyer.createCaseModal.title")}
                </h2>
                <p className="text-tiber-100 mt-1 text-sm">
                  {t("lawyer.createCaseModal.subtitle")}
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={loading || creatingClient}
                className="text-white/80 hover:text-white cursor-pointer transition-colors p-2 hover:bg-white/10 rounded-xl disabled:opacity-50"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center justify-center mt-6 gap-2">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <React.Fragment key={step.id}>
                    <button
                      onClick={() => {
                        if (step.id < currentStep) setCurrentStep(step.id);
                        else if (step.id === 2 && canProceedToStep2)
                          setCurrentStep(2);
                        else if (
                          step.id === 3 &&
                          canProceedToStep2 &&
                          canProceedToStep3
                        )
                          setCurrentStep(3);
                      }}
                      disabled={
                        (step.id === 2 && !canProceedToStep2) ||
                        (step.id === 3 &&
                          (!canProceedToStep2 || !canProceedToStep3))
                      }
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all cursor-pointer ${
                        isActive
                          ? "bg-white text-tiber-700 shadow-lg"
                          : isCompleted
                            ? "bg-white/30 text-white hover:bg-white/40"
                            : "bg-white/10 text-white/60 cursor-not-allowed"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckIcon className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                      <span className="font-medium text-sm hidden sm:inline">
                        {t(
                          `lawyer.createCaseModal.steps.${step.name}`,
                          step.name
                        )}
                      </span>
                    </button>
                    {index < STEPS.length - 1 && (
                      <div
                        className={`w-8 h-0.5 ${
                          currentStep > step.id ? "bg-white" : "bg-white/30"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Form content */}
          <div className="p-4 sm:p-8 max-h-[60vh] overflow-y-auto">
            <AnimatePresence mode="wait">
              {/* Step 1: Client Selection */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-juridico">
                      {t("lawyer.createCaseModal.selectClient")}
                    </h3>
                    {!isCreatingNewClient && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsCreatingNewClient(true)}
                        className="text-atlantis-600 hover:text-atlantis-700"
                      >
                        <UserPlusIcon className="h-4 w-4 mr-2" />
                        {t("lawyer.createCaseModal.newClient")}
                      </Button>
                    )}
                  </div>

                  {isCreatingNewClient ? (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="bg-romance-50 rounded-2xl p-6 border border-tiber-200 space-y-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-juridico">
                          {t("lawyer.createCaseModal.newClientForm")}
                        </h4>
                        <button
                          onClick={() => setIsCreatingNewClient(false)}
                          className="text-titan hover:text-tiber-700 cursor-pointer"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label={t(
                            "lawyer.createCaseModal.clientForm.fullName"
                          )}
                          value={newClientForm.full_name}
                          onChange={(e) =>
                            setNewClientForm({
                              ...newClientForm,
                              full_name: e.target.value,
                            })
                          }
                          placeholder="Juan Pérez"
                        />
                        <Input
                          label={t("lawyer.createCaseModal.clientForm.email")}
                          type="email"
                          value={newClientForm.email}
                          onChange={(e) =>
                            setNewClientForm({
                              ...newClientForm,
                              email: e.target.value,
                            })
                          }
                          placeholder="juan@ejemplo.com"
                        />
                        <div>
                          <label className="block text-sm font-medium text-tiber-700 mb-1">
                            {t("lawyer.createCaseModal.clientForm.phone")}
                          </label>
                          <PhoneInput
                            international
                            countryCallingCodeEditable={false}
                            defaultCountry="DO"
                            value={newClientForm.phone}
                            onChange={(value) =>
                              setNewClientForm({
                                ...newClientForm,
                                phone: value || "",
                              })
                            }
                            className="phone-input-create-case"
                          />
                          <style>{`
                            .phone-input-create-case {
                              display: flex;
                              align-items: center;
                              border: 1px solid var(--color-tiber-200);
                              border-radius: 0.5rem;
                              padding: 0.625rem 0.75rem;
                              transition: all 0.2s;
                            }
                            .phone-input-create-case:focus-within {
                              border-color: var(--color-tiber-500);
                              box-shadow: 0 0 0 2px rgba(20, 125, 122, 0.2);
                            }
                            .phone-input-create-case .PhoneInputCountry {
                              margin-right: 0.5rem;
                            }
                            .phone-input-create-case .PhoneInputInput {
                              border: none;
                              outline: none;
                              flex: 1;
                              font-size: 0.875rem;
                            }
                          `}</style>
                        </div>
                        <Select
                          label={t("lawyer.createCaseModal.clientForm.country")}
                          value={newClientForm.country}
                          onChange={(e) =>
                            setNewClientForm({
                              ...newClientForm,
                              country: e.target.value,
                            })
                          }
                          options={countries.map((c) => ({
                              value: c.code,
                              label: i18n.language === "es" ? c.nameEs : c.nameEn,
                            }))}
                        />
                        <Select
                          label={t(
                            "lawyer.createCaseModal.clientForm.clientType"
                          )}
                          value={newClientForm.client_type}
                          onChange={(e) =>
                            setNewClientForm({
                              ...newClientForm,
                              client_type: e.target
                                .value as NewClientForm["client_type"],
                            })
                          }
                          options={[
                            {
                              value: "individual",
                              label: t("lawyer.clientTypes.individual"),
                            },
                            {
                              value: "business",
                              label: t("lawyer.clientTypes.business"),
                            },
                            {
                              value: "nonprofit",
                              label: t("lawyer.clientTypes.nonprofit"),
                            },
                          ]}
                        />
                        {newClientForm.client_type !== "individual" && (
                          <Input
                            label={t(
                              "lawyer.createCaseModal.clientForm.companyName"
                            )}
                            value={newClientForm.company_name}
                            onChange={(e) =>
                              setNewClientForm({
                                ...newClientForm,
                                company_name: e.target.value,
                              })
                            }
                            placeholder="Empresa S.A. de C.V."
                          />
                        )}
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setIsCreatingNewClient(false)}
                          disabled={creatingClient}
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          type="button"
                          onClick={handleCreateNewClient}
                          isLoading={creatingClient}
                        >
                          {t("lawyer.createCaseModal.createClient")}
                        </Button>
                      </div>
                    </motion.div>
                  ) : (
                    <>
                      {clientsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tiber-600"></div>
                        </div>
                      ) : clients.length > 0 ? (
                        <div className="grid grid-cols-1 gap-3 max-h-80 overflow-y-auto pr-2">
                          {clients.map((client) => (
                            <button
                              key={client.id}
                              type="button"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  clientId: client.id,
                                })
                              }
                              className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left cursor-pointer ${
                                formData.clientId === client.id
                                  ? "border-tiber-500 bg-tiber-50 shadow-md"
                                  : "border-tiber-200 hover:border-tiber-300 hover:bg-romance-50"
                              }`}
                            >
                              <div className="shrink-0 h-12 w-12 rounded-full bg-linear-to-br from-tiber-600 to-tiber-800 flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-juridico truncate">
                                  {client.profiles?.full_name || "Sin nombre"}
                                </p>
                                <p className="text-sm text-titan truncate">
                                  {client.profiles?.email || "Sin email"}
                                </p>
                              </div>
                              {formData.clientId === client.id && (
                                <CheckIcon className="h-6 w-6 text-atlantis-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-romance-50 rounded-2xl">
                          <UserIcon className="h-12 w-12 text-titan mx-auto mb-4" />
                          <p className="text-tiber-700 mb-4">
                            {t("lawyer.createCaseModal.noClients")}
                          </p>
                          <Button
                            type="button"
                            variant="primary"
                            onClick={() => setIsCreatingNewClient(true)}
                          >
                            <UserPlusIcon className="h-4 w-4 mr-2" />
                            {t("lawyer.createCaseModal.createFirstClient")}
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
              )}

              {/* Step 2: Case Details */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h3 className="text-lg font-semibold text-juridico mb-4">
                    {t("lawyer.createCaseModal.caseDetails")}
                  </h3>

                  <Input
                    label={t("lawyer.createCaseModal.caseTitle") + " *"}
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder={t("lawyer.createCaseModal.titlePlaceholder")}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label={t("lawyer.createCaseModal.practiceArea") + " *"}
                      value={formData.practiceArea}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          practiceArea: e.target
                            .value as CaseFormData["practice_area"],
                        })
                      }
                      options={practiceAreas.map((area) => ({
                        value: area.code,
                        label: area.name,
                      }))}
                    />

                    <Select
                      label={t("lawyer.createCaseModal.priority")}
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value })
                      }
                      options={[
                        {
                          value: "low",
                          label: t("lawyer.createCaseModal.priorities.low"),
                        },
                        {
                          value: "medium",
                          label: t("lawyer.createCaseModal.priorities.medium"),
                        },
                        {
                          value: "high",
                          label: t("lawyer.createCaseModal.priorities.high"),
                        },
                        {
                          value: "urgent",
                          label: t("lawyer.createCaseModal.priorities.urgent"),
                        },
                      ]}
                    />
                  </div>

                  <Textarea
                    label={t("lawyer.createCaseModal.description")}
                    value={formData.description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder={t(
                      "lawyer.createCaseModal.descriptionPlaceholder"
                    )}
                    rows={4}
                  />
                </motion.div>
              )}

              {/* Step 3: Additional Info */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h3 className="text-lg font-semibold text-juridico mb-4">
                    {t("lawyer.createCaseModal.additionalInfo")}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-tiber-700 mb-2">
                        <ClockIcon className="h-4 w-4 inline mr-2" />
                        {t("lawyer.createCaseModal.estimatedHours")}
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.estimatedHours}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            estimatedHours: e.target.value,
                          })
                        }
                        placeholder="10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-tiber-700 mb-2">
                        <TagIcon className="h-4 w-4 inline mr-2" />
                        {t("lawyer.createCaseModal.tags")}
                      </label>
                      <div className="flex gap-2">
                        <Input
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddTag();
                            }
                          }}
                          placeholder={t("lawyer.createCaseModal.addTag")}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={handleAddTag}
                        >
                          +
                        </Button>
                      </div>
                      {formData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {formData.tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-tiber-100 text-tiber-700 rounded-full text-sm"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="hover:text-tiber-900"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <Textarea
                    label={t("lawyer.createCaseModal.initialNotes")}
                    value={formData.notes}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder={t("lawyer.createCaseModal.notesPlaceholder")}
                    rows={4}
                  />

                  {/* Case Summary */}
                  <div className="bg-linear-to-r from-tiber-50 to-atlantis-50 rounded-2xl p-6 border border-tiber-200">
                    <h4 className="font-semibold text-tiber-900 mb-4">
                      {t("lawyer.createCaseModal.summary")}
                    </h4>
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <dt className="text-tiber-600">
                          {t("lawyer.createCaseModal.client")}
                        </dt>
                        <dd className="font-medium text-tiber-900">
                          {clients.find((c) => c.id === formData.clientId)
                            ?.profiles?.full_name || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-tiber-600">
                          {t("lawyer.createCaseModal.caseTitle")}
                        </dt>
                        <dd className="font-medium text-tiber-900">
                          {formData.title || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-tiber-600">
                          {t("lawyer.createCaseModal.practiceArea")}
                        </dt>
                        <dd className="font-medium text-tiber-900 capitalize">
                          {practiceAreas.find(
                            (a) => a.code === formData.practiceArea
                          )?.name || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-tiber-600">
                          {t("lawyer.createCaseModal.priority")}
                        </dt>
                        <dd className="font-medium text-tiber-900 capitalize">
                          {t(
                            `lawyer.createCaseModal.priorities.${formData.priority}`
                          )}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer with navigation */}
          <div className="px-8 py-6 bg-romance-50 border-t border-tiber-200 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={currentStep === 1 ? handleClose : prevStep}
              disabled={loading || creatingClient}
            >
              {currentStep === 1 ? (
                t("common.cancel")
              ) : (
                <>
                  <ChevronLeftIcon className="h-4 w-4 mr-2" />
                  {t("common.back")}
                </>
              )}
            </Button>

            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={nextStep}
                disabled={
                  (currentStep === 1 && !canProceedToStep2) ||
                  (currentStep === 2 && !canProceedToStep3)
                }
              >
                {t("common.next")}
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                isLoading={loading}
                disabled={!canProceedToStep2 || !canProceedToStep3}
              >
                {loading
                  ? t("lawyer.createCaseModal.creating")
                  : t("lawyer.createCaseModal.createCase")}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
